/**
 * CREATIVE DETONATOR - Unified Image Preview Modal
 * Works across Generator, Dashboard Ideas, and Gallery pages
 *
 * Features:
 * - Consistent window-titlebar design
 * - Navigation (prev/next) for multiple images
 * - Keyboard shortcuts (Escape, Arrow keys)
 * - Download functionality
 * - Optional caption support
 * v3 - Uses modal-factory.js for modal creation
 */

import { createModal } from './modal-factory.js';
import { onDocument, cleanupScope } from '../core/events.js';
import { downloadImage } from '../core/download.js';

// ============================================================================
// MODAL CONTENT (body/footer only - titlebar handled by factory)
// ============================================================================

const MODAL_BODY_HTML = `
<div class="image-preview-container">
  <img id="image-preview-img" src="" alt="Preview" />
</div>
`;

const MODAL_FOOTER_HTML = `
<div class="image-preview-footer-left">
  <button class="btn btn-ghost preview-nav-btn" id="preview-prev" title="Previous (←)">
    <svg viewBox="0 0 12 12" width="12" height="12" style="margin-right:4px;">
      <path d="M8 2L4 6l4 4" stroke="currentColor" stroke-width="2" fill="none"/>
    </svg>
    PREV
  </button>
  <span class="preview-counter" id="preview-counter"></span>
  <button class="btn btn-ghost preview-nav-btn" id="preview-next" title="Next (→)">
    NEXT
    <svg viewBox="0 0 12 12" width="12" height="12" style="margin-left:4px;">
      <path d="M4 2l4 4-4 4" stroke="currentColor" stroke-width="2" fill="none"/>
    </svg>
  </button>
</div>
<button class="btn btn-ghost" id="image-preview-download">
  <svg viewBox="0 0 12 12" width="12" height="12" style="margin-right:6px;">
    <path d="M6 0v8M2 5l4 5 4-5" stroke="currentColor" stroke-width="2" fill="none"/>
  </svg>
  DOWNLOAD
</button>
`;

// ============================================================================
// STATE
// ============================================================================

let imagePreviewModal = null;
let currentImages = [];     // Array of {url, caption?, alt?} or just URLs
let currentIndex = 0;
let isInitialized = false;

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize the unified image preview modal
 * Call this once on page load
 */
export function initImagePreview() {
  if (isInitialized) return;

  // Create modal using factory (handles close button, overlay click, escape key)
  imagePreviewModal = createModal({
    id: 'image-preview-modal',
    title: 'IMAGE PREVIEW',
    content: MODAL_BODY_HTML,
    footerContent: MODAL_FOOTER_HTML,
    className: 'image-preview-modal',
  });

  if (!imagePreviewModal) {
    console.warn('[ImagePreview] Failed to create modal');
    return;
  }

  // Navigation buttons
  document.getElementById('preview-prev')?.addEventListener('click', () => navigate(-1));
  document.getElementById('preview-next')?.addEventListener('click', () => navigate(1));

  // Download button
  document.getElementById('image-preview-download')?.addEventListener('click', downloadCurrent);

  // Note: Keyboard navigation is registered per open/close cycle
  // See openPreview() for listener setup and closePreview() for cleanup

  isInitialized = true;
  console.log('[ImagePreview] Initialized');
}

// ============================================================================
// OPEN / CLOSE
// ============================================================================

/**
 * Open the preview modal
 * @param {string|string[]|Object[]} images - Single URL, array of URLs, or array of {url, caption?, alt?}
 * @param {number} startIndex - Index to start at (default 0)
 */
export function openPreview(images, startIndex = 0) {
  if (!imagePreviewModal) {
    initImagePreview();
    if (!imagePreviewModal) return;
  }

  // Normalize input to array of objects
  if (typeof images === 'string') {
    currentImages = [{ url: images }];
  } else if (Array.isArray(images)) {
    currentImages = images.map(img => {
      if (typeof img === 'string') return { url: img };
      return img;
    });
  } else {
    currentImages = [images];
  }

  if (currentImages.length === 0) {
    console.warn('[ImagePreview] No images to display');
    return;
  }

  currentIndex = Math.max(0, Math.min(startIndex, currentImages.length - 1));

  updateDisplay();
  imagePreviewModal.open();

  // Register keyboard navigation (Arrow keys - Escape handled by factory)
  // Uses scoped listener for automatic cleanup on close
  onDocument('keydown', handleKeyboard, 'image-preview');

  console.log('[ImagePreview] Opened with', currentImages.length, 'images, starting at index', currentIndex);
}

/**
 * Close the preview modal
 */
export function closePreview() {
  if (!imagePreviewModal) return;

  imagePreviewModal.close();

  // Cleanup scoped event listeners
  cleanupScope('image-preview');

  // Clear state
  currentImages = [];
  currentIndex = 0;

  console.log('[ImagePreview] Closed');
}

// ============================================================================
// NAVIGATION
// ============================================================================

/**
 * Navigate to previous/next image
 * @param {number} direction - -1 for previous, 1 for next
 */
function navigate(direction) {
  const newIndex = currentIndex + direction;
  if (newIndex >= 0 && newIndex < currentImages.length) {
    currentIndex = newIndex;
    updateDisplay();
  }
}

/**
 * Update the display with current image
 */
function updateDisplay() {
  const img = document.getElementById('image-preview-img');
  const caption = document.getElementById('preview-caption');
  const counter = document.getElementById('preview-counter');
  const prevBtn = document.getElementById('preview-prev');
  const nextBtn = document.getElementById('preview-next');
  const footerLeft = document.querySelector('.image-preview-footer-left');

  const currentImage = currentImages[currentIndex];

  // Update image
  if (img) {
    img.src = currentImage.url;
    img.alt = currentImage.alt || 'Preview';
  }

  // Update caption (if element exists)
  if (caption) {
    caption.textContent = currentImage.caption || '';
  }

  // Update counter
  if (counter) {
    counter.textContent = currentImages.length > 1
      ? `${currentIndex + 1} / ${currentImages.length}`
      : '';
  }

  // Update navigation visibility - hide entire footer-left section if single image
  const showNav = currentImages.length > 1;
  if (footerLeft) {
    footerLeft.style.display = showNav ? 'flex' : 'none';
  }

  // Disable at bounds
  if (prevBtn) prevBtn.disabled = currentIndex === 0;
  if (nextBtn) nextBtn.disabled = currentIndex === currentImages.length - 1;
}

// ============================================================================
// KEYBOARD HANDLING
// ============================================================================

/**
 * Handle keyboard events (Arrow keys only - Escape handled by factory)
 */
function handleKeyboard(e) {
  if (!imagePreviewModal?.isOpen) return;

  switch (e.key) {
    case 'ArrowLeft':
      navigate(-1);
      e.preventDefault();
      break;
    case 'ArrowRight':
      navigate(1);
      e.preventDefault();
      break;
  }
}

// ============================================================================
// DOWNLOAD
// ============================================================================

/**
 * Download the current image using centralized download utility
 * Handles R2 CORS issues via backend proxy
 */
async function downloadCurrent() {
  const currentImage = currentImages[currentIndex];
  if (!currentImage?.url) return;

  // Use centralized download utility (handles R2 proxy automatically)
  const timestamp = Date.now();
  const filename = `creative-detonator-${timestamp}`;
  await downloadImage(currentImage.url, filename);
}

// ============================================================================
// UTILITY EXPORTS
// ============================================================================

/**
 * Check if the preview modal is currently open
 */
export function isPreviewOpen() {
  return imagePreviewModal?.isOpen ?? false;
}

/**
 * Get current image index
 */
export function getCurrentIndex() {
  return currentIndex;
}

/**
 * Get total image count
 */
export function getImageCount() {
  return currentImages.length;
}

// ============================================================================
// GLOBAL EXPORTS (for non-module usage)
// ============================================================================

window.ImagePreview = {
  init: initImagePreview,
  open: openPreview,
  close: closePreview,
  isOpen: isPreviewOpen
};
